import { api } from "@shared/api/api-factory"
import type { User } from '@/shared/schemas/auth-schema.ts'
import type { PaginatedData, ApiResponse } from "@/shared/schemas/api-schema"
import { USER_STATUS } from "@shared/constants/user-constants"
import { CreateUserSchema, UpdateUserSchema, type CreateUserPayload, type UpdateUserPayload } from "@/shared/schemas/user-schema"
import { PERMISSION_KEY_TO_NUMBER } from "@shared/constants/user-constants"

const USERS_BASE_URL = "/admin/users"

export interface UserFilters {
  tenantId?: string
  roleName?: string // Changed from roleId to roleName
  userName?: string
  status?: typeof USER_STATUS.LOCKED | typeof USER_STATUS.ENABLED
  query?: string
  workstationId?: number // Filter by workstation ID
}

export type CreateUserRequest = CreateUserPayload
export type UpdateUserRequest = UpdateUserPayload

/**
 * Users API parameters
 * Note: Uses sortField/sortAscending instead of sortBy/sortDirection
 */
export interface UsersApiParams extends UserFilters {
  pageNumber?: number
  pageSize?: number
  sortField?: string
  sortAscending?: boolean
}

export const usersApi = {
  getUsers: async (
    params: UsersApiParams,
  ): Promise<ApiResponse<PaginatedData<User>>> => {
    const requestParams = {
      pageNumber: params.pageNumber || 1,
      pageSize: params.pageSize || 10,
      ...(params.query && { query: params.query }),
      ...(params.userName && { userName: params.userName }),
      ...(params.tenantId && { tenantId: params.tenantId }),
      ...(params.roleName && { roleName: params.roleName }),
      ...(params.status && { status: params.status }),
      ...(params.workstationId && { workstationId: params.workstationId }),
      ...(params.sortField && { sortField: params.sortField }),
      ...(params.sortAscending !== undefined && { sortAscending: params.sortAscending }),
    }

    return api.get<PaginatedData<User>>(USERS_BASE_URL, requestParams)
  },

  getUserById: async (id: string): Promise<ApiResponse<User>> => {
    return api.get<User>(`${USERS_BASE_URL}/${id}`)
  },

  getUserPermissions: async (id: string): Promise<ApiResponse<number[]>> => {
    return api.get<number[]>(`${USERS_BASE_URL}/${id}/permissions`)
  },

  getUserWorkstations: async (id: string): Promise<ApiResponse<number[]>> => {
    return api.get<number[]>(`${USERS_BASE_URL}/${id}/workstations`)
  },

  createUser: async (userData: CreateUserRequest): Promise<ApiResponse<User>> => {
    const parsed = CreateUserSchema.safeParse(userData)
    if (!parsed.success) {
      throw parsed.error
    }

    const payload = parsed.data.permissions
      ? { ...parsed.data, permissions: parsed.data.permissions.map((p) => PERMISSION_KEY_TO_NUMBER[p as keyof typeof PERMISSION_KEY_TO_NUMBER]) }
      : parsed.data
    return api.post<User>(USERS_BASE_URL, payload)
  },

  updateUser: async (id: string, userData: UpdateUserRequest): Promise<ApiResponse<User>> => {
    const parsed = UpdateUserSchema.safeParse(userData)
    if (!parsed.success) {
      throw parsed.error
    }
    const payload = parsed.data.permissions
      ? { ...parsed.data, permissions: parsed.data.permissions.map((p) => PERMISSION_KEY_TO_NUMBER[p as keyof typeof PERMISSION_KEY_TO_NUMBER]) }
      : parsed.data
    return api.put<User>(`${USERS_BASE_URL}/${id}`, payload)
  },

  deleteUser: async (id: string): Promise<ApiResponse<void>> => {
    return api.delete(`${USERS_BASE_URL}/${id}`)
  },

  getCurrentUserPermissions: async (): Promise<ApiResponse<number[]>> => {
    return api.get<number[]>(`/user/permissions`)
  },

  /**
   * Change current user's password (self-service, requires old password)
   * POST /user/change-password
   */
  changePassword: async (oldPassword: string, newPassword: string, confirmPassword: string): Promise<ApiResponse<void>> => {
    return api.post<void>('/user/change-password', {
      oldPassword,
      newPassword,
      confirmPassword
    })
  },
}


